import React, { useState } from "react";

import { Button } from "@/components/primitives/Button";
import { Dialog } from "@/components/primitives/Dialog";
import { Input } from "@/components/primitives/Input";
import { Select } from "@/components/primitives/Select";
import { TextArea } from "@/components/primitives/TextArea";
import { useDbConnections } from "@/hooks/useDbConnections";
import type {
  ComparisonConfig,
  DatabaseConfig,
  DbConnection,
  DbConnectionCreate,
  QueryMode,
} from "@/types";

import { DbConnectionForm } from "./DbConnectionForm";

interface DatabaseStepFieldsProps {
  dbConfig: DatabaseConfig | null;
  onChange: (config: DatabaseConfig | null) => void;
}

const QUERY_MODE_OPTIONS: { value: QueryMode; label: string }[] = [
  { value: "raw", label: "Raw SQL" },
  { value: "table_select", label: "Table select" },
  { value: "function", label: "Function" },
  { value: "mongo_find", label: "MongoDB find" },
  { value: "mongo_pipeline", label: "MongoDB aggregation" },
];

const COMPARISON_MODE_OPTIONS: { value: DatabaseConfig["comparison"]["mode"]; label: string }[] = [
  { value: "not_empty", label: "Result is not empty" },
  { value: "row_count", label: "Row count" },
  { value: "scalar", label: "Scalar value" },
  { value: "json_path", label: "JSONPath" },
  { value: "contains_row", label: "Contains row" },
];

const OPERATOR_OPTIONS: { value: ComparisonConfig["operator"]; label: string }[] = [
  { value: "eq", label: "equals" },
  { value: "ne", label: "not equals" },
  { value: "gt", label: ">" },
  { value: "gte", label: ">=" },
  { value: "lt", label: "<" },
  { value: "lte", label: "<=" },
  { value: "contains", label: "contains" },
  { value: "not_empty", label: "is not empty" },
];

const RAW_QUERY_EXAMPLES = [
  {
    label: "Active flows count",
    query: "SELECT count(*) FROM flows WHERE is_active = true",
  },
  {
    label: "Recent flows",
    query: "SELECT name, is_active FROM flows ORDER BY created_at DESC LIMIT 5",
  },
  {
    label: "Runs by status",
    query: "SELECT status, count(*) FROM runs GROUP BY status",
  },
  {
    label: "Database steps",
    query: "SELECT count(*) FROM steps WHERE step_type = 'database'",
  },
];

const emptyConfig = (): DatabaseConfig => ({
  db_type: "postgresql",
  connection: { connection_id: null, overrides: {} },
  query_mode: "raw",
  raw_query: { query: "" },
  comparison: { mode: "not_empty", operator: "not_empty", expected_value: null },
});

const safeJsonParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

export const DatabaseStepFields: React.FC<DatabaseStepFieldsProps> = ({ dbConfig, onChange }) => {
  const config = dbConfig ?? emptyConfig();
  const { connections, create, update, test, deploy, resetToken, uninstall } = useDbConnections();
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<DbConnection | null>(null);
  const [deployingConnectionIds, setDeployingConnectionIds] = useState<Set<string>>(new Set());
  const [resettingConnectionId, setResettingConnectionId] = useState<string | null>(null);
  const [uninstallingConnectionId, setUninstallingConnectionId] = useState<string | null>(null);

  const updateConfig = (patch: Partial<DatabaseConfig>) => {
    onChange({ ...config, ...patch });
  };

  const handleConnectionChange = (connectionId: string) => {
    updateConfig({
      connection: { ...config.connection, connection_id: connectionId || null },
    });
  };

  const handleOpenCreate = () => {
    setEditingConnection(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = () => {
    const selected = connections.find((c) => c.id === config.connection.connection_id);
    if (selected) {
      setEditingConnection(selected);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingConnection(null);
  };

  const handleSaveConnection = async (payload: DbConnectionCreate): Promise<boolean> => {
    if (editingConnection) {
      const result = await update(editingConnection.id, payload);
      if (result) {
        handleCloseModal();
        return true;
      }
      return false;
    }
    const created = await create(payload);
    if (created) {
      updateConfig({
        db_type: created.db_type,
        connection: { ...config.connection, connection_id: created.id },
      });
      handleCloseModal();
      return true;
    }
    return false;
  };

  const handleTestConnection = async () => {
    const connectionId = config.connection.connection_id;
    if (!connectionId) {
      setTestMessage("Select a connection to test");
      return;
    }
    setTestMessage("Testing…");
    const result = await test(connectionId);
    setTestMessage(result ? result.message : "Test failed");
  };

  const handleDeploy = async (connection: DbConnection): Promise<boolean> => {
    if (deployingConnectionIds.has(connection.id)) {
      return false;
    }
    setDeployingConnectionIds((prev) => new Set(prev).add(connection.id));
    const result = await deploy(connection.id);
    setDeployingConnectionIds((prev) => {
      const next = new Set(prev);
      next.delete(connection.id);
      return next;
    });
    return result !== null;
  };

  const handleResetToken = async (connection: DbConnection): Promise<boolean> => {
    setResettingConnectionId(connection.id);
    const result = await resetToken(connection.id);
    setResettingConnectionId(null);
    return result !== null;
  };

  const handleUninstall = async (connection: DbConnection): Promise<boolean> => {
    setUninstallingConnectionId(connection.id);
    const result = await uninstall(connection.id);
    setUninstallingConnectionId(null);
    return result !== null;
  };

  const connectionOptions = [
    { value: "", label: "-- Select a connection --" },
    ...connections.map((c) => ({ value: c.id, label: `${c.name} (${c.db_type})` })),
  ];

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h5 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {children}
    </h5>
  );

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 basis-60 flex-1">
          <Select
            label="Connection"
            value={config.connection.connection_id ?? ""}
            options={connectionOptions}
            onChange={(e) => handleConnectionChange(e.target.value)}
          />
        </div>
        <div className="flex shrink-0 gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={handleOpenCreate}>
            New
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleOpenEdit}
            disabled={!config.connection.connection_id}
          >
            Edit
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={handleTestConnection}>
            Test
          </Button>
        </div>
        {testMessage && (
          <p className="w-full text-sm text-slate-500 dark:text-slate-400" role="status">
            {testMessage}
          </p>
        )}
      </div>

      <Dialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingConnection ? "Edit connection" : "New connection"}
      >
        <DbConnectionForm
          connection={editingConnection}
          onSubmit={handleSaveConnection}
          onCancel={handleCloseModal}
          onDeploy={editingConnection ? () => handleDeploy(editingConnection) : undefined}
          onResetToken={editingConnection ? () => handleResetToken(editingConnection) : undefined}
          onUninstall={editingConnection ? () => handleUninstall(editingConnection) : undefined}
          isDeploying={editingConnection ? deployingConnectionIds.has(editingConnection.id) : false}
          isResettingToken={editingConnection ? resettingConnectionId === editingConnection.id : false}
          isUninstalling={editingConnection ? uninstallingConnectionId === editingConnection.id : false}
        />
      </Dialog>

      <div className="flex flex-col gap-4 border-t border-slate-100 pt-4 dark:border-slate-800">
        <SectionTitle>Query</SectionTitle>
        <Select
          label="Query mode"
          value={config.query_mode}
          options={QUERY_MODE_OPTIONS}
          onChange={(e) => {
            const mode = e.target.value as QueryMode;
            const patch: Partial<DatabaseConfig> = { query_mode: mode };
            if (mode === "raw") patch.raw_query = { query: "" };
            if (mode === "function") patch.function_query = { name: "", args: [] };
            if (mode === "table_select") patch.table_select_query = { table_name: "", selected_columns: [] };
            if (mode === "mongo_find" || mode === "mongo_pipeline") {
              patch.mongo_query = {
                collection: "",
                filter: {},
                projection: null,
                pipeline: null,
              };
            }
            updateConfig(patch);
          }}
        />

        {config.query_mode === "raw" && (
          <div className="flex flex-col gap-2">
            <TextArea
              label="SQL query"
              value={config.raw_query?.query ?? ""}
              onChange={(e) =>
                updateConfig({
                  raw_query: { query: e.target.value },
                })
              }
              placeholder="SELECT count(*) FROM flows WHERE is_active = true"
              rows={5}
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500 dark:text-slate-400">Examples:</span>
              <div className="flex flex-wrap gap-2">
                {RAW_QUERY_EXAMPLES.map((example) => (
                  <Button
                    key={example.label}
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      updateConfig({
                        raw_query: { query: example.query },
                      })
                    }
                    title={example.query}
                  >
                    {example.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {config.query_mode === "function" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Schema (optional)"
              value={config.function_query?.schema_name ?? ""}
              onChange={(e) =>
                updateConfig({
                  function_query: {
                    ...(config.function_query ?? { name: "", args: [] }),
                    schema_name: e.target.value || null,
                  },
                })
              }
              placeholder="public"
            />
            <Input
              label="Function name"
              value={config.function_query?.name ?? ""}
              onChange={(e) =>
                updateConfig({
                  function_query: {
                    ...(config.function_query ?? { schema_name: null, args: [] }),
                    name: e.target.value,
                  },
                })
              }
            />
            <TextArea
              className="sm:col-span-2"
              label="Arguments (JSON array)"
              value={JSON.stringify(config.function_query?.args ?? [], null, 2)}
              onChange={(e) => {
                const parsed = safeJsonParse(e.target.value);
                if (Array.isArray(parsed)) {
                  updateConfig({
                    function_query: {
                      ...(config.function_query ?? { schema_name: null, name: "" }),
                      args: parsed,
                    },
                  });
                }
              }}
              rows={3}
            />
          </div>
        )}

        {config.query_mode === "table_select" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Table name"
              value={config.table_select_query?.table_name ?? ""}
              onChange={(e) =>
                updateConfig({
                  table_select_query: {
                    ...(config.table_select_query ?? { selected_columns: [] }),
                    table_name: e.target.value,
                  },
                })
              }
            />
            <Input
              label="Columns (comma separated, leave empty for *)"
              value={(config.table_select_query?.selected_columns ?? []).join(", ")}
              onChange={(e) =>
                updateConfig({
                  table_select_query: {
                    ...(config.table_select_query ?? { table_name: "" }),
                    selected_columns: e.target.value
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
            <Input
              className="sm:col-span-2"
              label="WHERE clause (optional)"
              value={config.table_select_query?.where_clause ?? ""}
              onChange={(e) =>
                updateConfig({
                  table_select_query: {
                    ...(config.table_select_query ?? { table_name: "", selected_columns: [] }),
                    where_clause: e.target.value || null,
                  },
                })
              }
              placeholder="active = true"
            />
          </div>
        )}

        {(config.query_mode === "mongo_find" || config.query_mode === "mongo_pipeline") && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Collection"
              value={config.mongo_query?.collection ?? ""}
              onChange={(e) =>
                updateConfig({
                  mongo_query: {
                    ...(config.mongo_query ?? { filter: {}, projection: null, pipeline: null }),
                    collection: e.target.value,
                  },
                })
              }
            />
            {config.query_mode === "mongo_find" && (
              <>
                <TextArea
                  className="sm:col-span-2"
                  label="Filter (JSON object)"
                  value={JSON.stringify(config.mongo_query?.filter ?? {}, null, 2)}
                  onChange={(e) => {
                    const parsed = safeJsonParse(e.target.value);
                    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                      updateConfig({
                        mongo_query: {
                          ...(config.mongo_query ?? { collection: "", projection: null, pipeline: null }),
                          filter: parsed as Record<string, unknown>,
                        },
                      });
                    }
                  }}
                  rows={3}
                />
                <TextArea
                  className="sm:col-span-2"
                  label="Projection (JSON object, optional)"
                  value={JSON.stringify(config.mongo_query?.projection ?? {}, null, 2)}
                  onChange={(e) => {
                    const parsed = safeJsonParse(e.target.value);
                    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                      updateConfig({
                        mongo_query: {
                          ...(config.mongo_query ?? { collection: "", filter: {}, pipeline: null }),
                          projection: parsed as Record<string, unknown>,
                        },
                      });
                    }
                  }}
                  rows={3}
                />
              </>
            )}
            {config.query_mode === "mongo_pipeline" && (
              <TextArea
                className="sm:col-span-2"
                label="Pipeline (JSON array)"
                value={JSON.stringify(config.mongo_query?.pipeline ?? [], null, 2)}
                onChange={(e) => {
                  const parsed = safeJsonParse(e.target.value);
                  if (Array.isArray(parsed)) {
                    updateConfig({
                      mongo_query: {
                        ...(config.mongo_query ?? { collection: "", filter: {}, projection: null }),
                        pipeline: parsed,
                      },
                    });
                  }
                }}
                rows={5}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 border-t border-slate-100 pt-4 dark:border-slate-800">
        <SectionTitle>Comparison</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Compare"
            value={config.comparison.mode}
            options={COMPARISON_MODE_OPTIONS}
            onChange={(e) =>
              updateConfig({
                comparison: {
                  ...config.comparison,
                  mode: e.target.value as ComparisonConfig["mode"],
                },
              })
            }
          />
          <Select
            label="Operator"
            value={config.comparison.operator}
            options={OPERATOR_OPTIONS}
            onChange={(e) =>
              updateConfig({
                comparison: {
                  ...config.comparison,
                  operator: e.target.value as ComparisonConfig["operator"],
                },
              })
            }
          />
          {config.comparison.mode !== "not_empty" && (
            <Input
              className="sm:col-span-2"
              label="Expected value (JSON)"
              value={JSON.stringify(config.comparison.expected_value)}
              onChange={(e) => {
                const parsed = safeJsonParse(e.target.value);
                updateConfig({
                  comparison: {
                    ...config.comparison,
                    expected_value: parsed === undefined ? e.target.value : parsed,
                  },
                });
              }}
            />
          )}
        </div>
        {config.comparison.mode === "json_path" && (
          <Input
            label="JSONPath"
            value={config.comparison.json_path ?? ""}
            onChange={(e) =>
              updateConfig({
                comparison: {
                  ...config.comparison,
                  json_path: e.target.value || null,
                },
              })
            }
            placeholder="$.[0].status"
          />
        )}
      </div>
    </div>
  );
};
